import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useStore } from 'store/useStore'
import { Timer } from 'sections/LKSection/modules/OrderModule/components/Timer/Timer'
import { OrderKeys } from 'sections/LKSection/modules/OrderModule/data'
import { Order } from 'sections/LKSection/modules/OrderModule/stores/entity/Order'
import { OrderPaymentStatus } from 'sections/LKSection/modules/OrderModule/types'
import { Info } from 'components/ui/Info/Info'
import { Button, Flex, Text } from 'components/uiKit'
import { getOrderPaymentRoute } from 'utils/helpers/routes/getRoutes'
import { useAsyncEffect } from 'utils/hooks/useAsyncEffect'

import { OrderItemStatusHeader } from '../OrderItemStatusHeader/OrderItemStatusHeader'

const text = {
  [OrderPaymentStatus.none]: 'Не оплачена',
  [OrderPaymentStatus.pending]: 'Оплата в обработке',
  [OrderPaymentStatus.error]: 'Ошибка оплаты',
  [OrderPaymentStatus.paid]: 'Оплачена',
}

type OrderStatusWaitingPaymentProps = {
  paymentStatus: OrderPaymentStatus | null
  hasErrorWithPayment?: boolean
  order: Order
}

export const OrderStatusWaitingPayment = observer(
  ({ paymentStatus, hasErrorWithPayment, order }: OrderStatusWaitingPaymentProps) => {
    const { viewOrderUiStore: uiStore } = useStore()

    const navigate = useNavigate()
    const { t } = useTranslation()

    const [timeLeft, setTimeLeft] = useState(0)
    const [loading, setLoading] = useState(false)

    const hasError = paymentStatus === OrderPaymentStatus.error || hasErrorWithPayment

    useAsyncEffect(async () => {
      if (order.id) {
        setLoading(true)
        const seconds = await uiStore.getPaymentDeadline(order.id)
        setLoading(false)
        seconds && setTimeLeft(seconds)
      }
    }, [])

    const getKey = (key: string) => `${OrderKeys.orderItemPage}.${key}`

    return (
      <Flex vertical gap={16}>
        <OrderItemStatusHeader
          type={hasError ? 'failure' : 'wait'}
          title="Шаг 2"
          subTitle={hasError ? t(getKey('Ошибка оплаты')) : t(getKey(text[paymentStatus ?? OrderPaymentStatus.none]))}
          isActive
        />

        {Boolean(timeLeft) && (
          <>
            {hasError ? (
              <Info type="error">{t(getKey('Ваша оплата не прошла'))}</Info>
            ) : (
              <Text>{t(getKey('Заявка ожидает оплаты'))}</Text>
            )}

            <Text>{t(getKey('Оплатите заявку'))}</Text>
          </>
        )}

        {!timeLeft && <Text>{t(getKey('Время на оплату вышло'))}</Text>}

        {Boolean(timeLeft) && <Timer type="timeLeft" timeLeftInSeconds={timeLeft} loading={loading} />}

        {(uiStore.orderPaymentStatus === OrderPaymentStatus.none ||
          uiStore.orderPaymentStatus === OrderPaymentStatus.error) && (
          <Flex>
            <Button
              onClick={() => uiStore.selectedOrder?.id && navigate(getOrderPaymentRoute(uiStore.selectedOrder?.id))}
              disabled={!timeLeft}
            >
              {t(getKey('Внести гарантийный взнос'))}
            </Button>
          </Flex>
        )}
      </Flex>
    )
  },
)
